import { useSyncExternalStore } from 'react';

type OfflineState = {
  status: 'preparing' | 'ready' | 'unavailable' | 'error';
  update: boolean;
};

let activatedForUpdate = false;
let dismissedUpdate = false;
export function dismissOfflineUpdate() {
  dismissedUpdate = true;
  publish({ update: false });
}

let registrationForUpdate: ServiceWorkerRegistration | undefined;

let state: OfflineState = { status: 'preparing', update: false };
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
function publish(change: Partial<OfflineState>) {
  state = { ...state, ...change };
  if (activatedForUpdate) state.update = true;
  if (dismissedUpdate) state.update = false;
  for (const listener of listeners) listener();
}

export function useOfflineState() {
  return useSyncExternalStore(subscribe, () => state);
}

export function registerOfflineApp() {
  if (
    !import.meta.env.PROD ||
    !window.isSecureContext ||
    !('serviceWorker' in navigator)
  ) {
    publish({ status: 'unavailable' });
    return;
  }
  const initialController = navigator.serviceWorker.controller;
  let checkSequence = 0;
  const checkController = () => {
    const controller = navigator.serviceWorker.controller;
    if (!controller) return;
    if (initialController && initialController !== controller) {
      activatedForUpdate = true;
      publish({ update: true });
    }
    const sequence = ++checkSequence;
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => {
      channel.port1.close();
      if (sequence === checkSequence) publish({ status: 'error' });
    }, 5000);
    channel.port1.onmessage = (event) => {
      window.clearTimeout(timer);
      channel.port1.close();
      if (sequence === checkSequence)
        publish({ status: event.data?.ready ? 'ready' : 'error' });
    };
    controller.postMessage(
      {
        type: navigator.onLine ? 'OFFLINE_REPAIR' : 'OFFLINE_STATUS',
        entry: import.meta.url,
      },
      [channel.port2],
    );
  };
  // controllerchange never reloads the document or alters project persistence.
  navigator.serviceWorker.addEventListener('controllerchange', checkController);
  const observed = new WeakSet<ServiceWorkerRegistration>();
  const register = async () => {
    checkController();
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
      });
      registrationForUpdate = registration;
      if (!observed.has(registration)) {
        observed.add(registration);
        const update = () => publish({ update: !!registration.waiting });
        const watch = () => {
          const worker = registration.installing;
          worker?.addEventListener('statechange', () => {
            update();
            if (worker.state === 'redundant' && !navigator.serviceWorker.controller)
              publish({ status: 'error' });
          });
        };
        registration.addEventListener('updatefound', watch);
        watch();
      }
      publish({ update: !!registration.waiting });
      checkController();
    } catch {
      // A failed update does not invalidate an already cached working release.
      if (!navigator.serviceWorker.controller) publish({ status: 'error' });
    }
  };
  // Keep initial cache downloads off the editor's first render. Existing clients
  // can report cached readiness immediately while update checks wait for idle.
  checkController();
  let lastCheck = 0;
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    const start = () => {
      scheduled = false;
      lastCheck = Date.now();
      void register();
    };
    if ('requestIdleCallback' in window)
      window.requestIdleCallback(start, { timeout: 3000 });
    else setTimeout(start, 250);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
  window.addEventListener('online', schedule);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && navigator.onLine && Date.now() - lastCheck > 60000)
      schedule();
  });
  window.setInterval(
    () => {
      if (!document.hidden && navigator.onLine) schedule();
    },
    60 * 60 * 1000,
  );
}

/** Only the tab whose user requested the update may reload. */
export async function activateOfflineUpdate(canReload: () => boolean): Promise<void> {
  if (!navigator.onLine) throw Error('You’re offline. Try again when connected.');
  if (activatedForUpdate) {
    if (canReload()) window.location.reload();
    return;
  }
  const registration = registrationForUpdate;
  const worker = registration?.waiting;
  if (!worker) throw Error('Update unavailable. Reopen DigiTiles to check again.');
  await new Promise<void>((resolve, reject) => {
    const channel = new MessageChannel();
    const finish = (error?: string) => {
      clearTimeout(timer);
      channel.port1.close();
      navigator.serviceWorker.removeEventListener('controllerchange', changed);
      if (error) reject(Error(error));
      else resolve();
    };
    const changed = () => {
      if (navigator.serviceWorker.controller !== worker) return;
      activatedForUpdate = true;
      publish({ update: true });
      if (!canReload()) {
        finish('Update ready. Save your changes, then reload DigiTiles.');
        return;
      }
      finish();
      window.location.reload();
    };
    const timer = setTimeout(() => finish('Update took too long. Try again.'), 15000);
    navigator.serviceWorker.addEventListener('controllerchange', changed);
    channel.port1.onmessage = (event) => {
      if (event.data?.error) finish(event.data.error);
    };
    worker.postMessage({ type: 'ACTIVATE_UPDATE' }, [channel.port2]);
  });
}
